import {
  type AssetExposure,
  isOptionSymbol,
  positionNotionalUsd,
} from '@/features/home/lib/home-exposure'

/**
 * What changed in a brief card's data and what it does to the user's positions (Alex: "highlight
 * exactly what has changed in the data and how it has impacted the user's position"). Each card
 * turns its own numbers into a `changed` line, a `for you` line and whether the change is big
 * enough to light the card up. Pure (tested in __tests__/home-card-highlights.test.ts).
 */

/** The user's net USD delta in a coin: longs minus shorts, options left out. */
export function netDeltaUsd(exposure: AssetExposure | undefined): number {
  if (!exposure) return 0
  return exposure.positions
    .filter((risk) => !isOptionSymbol(risk.position.symbol))
    .reduce(
      (sum, risk) => sum + (risk.side === 'long' ? 1 : -1) * positionNotionalUsd(risk.position),
      0
    )
}

export interface FundingChange {
  /** Annualised percent a day ago and now. */
  from: number
  to: number
  flipped: boolean
  /** Big enough to call out (sign flip, or 15+ points). */
  strong: boolean
}

/**
 * Funding now against a day ago, from hourly FUNDING_OI candles (`fundingAprClose`, fractions).
 * Null when it barely moved (under 5 points a year) or there's no history.
 */
export function fundingChange(
  aprCloses: readonly number[],
  nowAprPercent: number | null
): FundingChange | null {
  const history = aprCloses.filter((value) => Number.isFinite(value))
  if (history.length < 2 || nowAprPercent === null) return null
  const from = history[0] * 100
  const to = nowAprPercent
  const flipped = Math.sign(from) !== Math.sign(to) && Math.abs(from - to) >= 2
  if (!flipped && Math.abs(to - from) < 5) return null
  return { from, to, flipped, strong: flipped || Math.abs(to - from) >= 15 }
}

/** Funding cost (positive) or income (negative) a day on `notionalUsd` held on `side`. */
export function fundingPerDayUsd(
  notionalUsd: number,
  side: 'long' | 'short',
  aprPercent: number
): number {
  const paid = side === 'long' ? aprPercent : -aprPercent
  return (notionalUsd * paid) / 100 / 365
}

export interface FlowRun {
  kind: 'streak' | 'flipped'
  direction: 'in' | 'out'
  /** Streak: days in a row ending today. Flipped: days of the other way before today. */
  days: number
}

/** A run of ETF flows the same way (3+ days), or today breaking a 3+ day run. */
export function etfFlowRun(flows: readonly number[]): FlowRun | null {
  const nonZero = flows.filter((value) => value !== 0)
  if (nonZero.length < 2) return null
  const last = nonZero[nonZero.length - 1]
  const sign = Math.sign(last)
  let streak = 0
  for (let i = nonZero.length - 1; i >= 0 && Math.sign(nonZero[i]) === sign; i--) streak++
  if (streak >= 3) return { kind: 'streak', direction: sign > 0 ? 'in' : 'out', days: streak }
  if (streak === 1) {
    let before = 0
    for (let i = nonZero.length - 2; i >= 0 && Math.sign(nonZero[i]) === -sign; i--) before++
    if (before >= 3) return { kind: 'flipped', direction: sign > 0 ? 'in' : 'out', days: before }
  }
  return null
}

export interface LiquidationBucket {
  mid: number
  /** Long notional liquidated if price falls here (USD). */
  long: number
  /** Short notional liquidated if price rises here (USD). */
  short: number
}

export interface Cluster {
  price: number
  usd: number
  /** Distance from the current price, percent (negative below). */
  distance: number
}

/**
 * The biggest pool of long liquidations below the price and of shorts above it, within
 * `range` (fraction) of the current price.
 */
export function liquidationClusters(
  buckets: readonly LiquidationBucket[],
  mid: number,
  range = 0.15
): { below: Cluster | null; above: Cluster | null; nearby: LiquidationBucket[] } {
  const nearby = buckets.filter((bucket) => Math.abs(bucket.mid - mid) / mid <= range)
  const pick = (side: 'long' | 'short'): Cluster | null => {
    const candidates = nearby.filter((bucket) =>
      side === 'long' ? bucket.mid < mid : bucket.mid > mid
    )
    const best = candidates.reduce<LiquidationBucket | null>(
      (top, bucket) => (!top || bucket[side] > top[side] ? bucket : top),
      null
    )
    if (!best || best[side] <= 0) return null
    return { price: best.mid, usd: best[side], distance: ((best.mid - mid) / mid) * 100 }
  }
  return { below: pick('long'), above: pick('short'), nearby }
}

/** The cluster that sits within `tolerance` (fraction) of a liquidation price, if any. */
export function clusterNear(
  liquidationPrice: number | null,
  clusters: readonly (Cluster | null)[],
  tolerance = 0.02
): Cluster | null {
  if (!liquidationPrice) return null
  return (
    clusters.find(
      (cluster): cluster is Cluster =>
        cluster !== null &&
        Math.abs(cluster.price - liquidationPrice) / liquidationPrice <= tolerance
    ) ?? null
  )
}

/** The listed expiry closest to `targetDays` out, from rows with a days-to-expiry field. */
export function closestExpiry<T extends { daysToExpiration: string }>(
  rows: readonly T[],
  targetDays = 30
): T | null {
  return rows.reduce<T | null>((best, row) => {
    const days = Number(row.daysToExpiration)
    if (!Number.isFinite(days) || days <= 0) return best
    if (!best) return row
    return Math.abs(days - targetDays) < Math.abs(Number(best.daysToExpiration) - targetDays)
      ? row
      : best
  }, null)
}
